import blogIndexRaw from "@/content/blog-index.json";
import type { BlogListItem, BlogPost } from "@/types/blog.types";

// Type for the blog index entries (metadata only)
interface BlogIndexEntry {
	id: number;
	title: string;
	description: string;
	date: string;
	slug: string;
	image: string;
	featured: boolean;
	tags: string[];
	keywords?: string[];
	filePath: string;
}

// Type for individual blog content files
interface BlogContentFile {
	id: number;
	slug: string;
	content: string;
	htmlContent: string;
}

const blogIndex = blogIndexRaw as BlogIndexEntry[];

// Glob import for content files to satisfy Vite's static analysis
const blogContentModules = import.meta.glob<{ default: BlogContentFile }>(
	"/src/content/compiled/*.json",
);

// Memoization cache for blog list
interface BlogCache {
	data: BlogListItem[];
	timestamp: number;
}

let blogListCache: BlogCache | null = null;
const CACHE_TTL = 60 * 1000; // 60 seconds

// Custom Errors
export class BlogNotFoundError {
	readonly _tag = "BlogNotFoundError";
	constructor(public readonly slug: string) {}
}

export class BlogImportError {
	readonly _tag = "BlogImportError";
	constructor(
		public readonly slug: string,
		public readonly error: unknown,
	) {}
}

/**
 * Check if a slug exists in the blog index
 */
export function isValidBlogSlug(slug: string): boolean {
	return blogIndex.some((entry) => entry.slug === slug);
}

/**
 * Get all blog posts sorted by date (newest first)
 * Uses memoization with TTL to prevent redundant processing while allowing updates
 */
export const getAllBlogs = async () => {
	const now = Date.now();

	// Return cached data if available and fresh
	if (blogListCache && now - blogListCache.timestamp < CACHE_TTL) {
		return blogListCache.data;
	}

	// Map the JSON index to BlogListItem
	const posts = blogIndex
		.map((entry) => ({
			id: entry.id, // Use the ID from the index
			title: entry.title,
			des: entry.description,
			date: entry.date,
			slug: entry.slug.replace(/^(\/|blog\/|\/blog\/)+/, ""),
			img: entry.image,
			featured: entry.featured || false,
			tags: entry.tags || [],
			keywords: entry.keywords || [],
		}))
		.sort((a, b) => {
			// Sort by date (newest first)
			const dateA = new Date(a.date);
			const dateB = new Date(b.date);
			return dateB.getTime() - dateA.getTime();
		});

	blogListCache = {
		data: posts,
		timestamp: now,
	};
	return posts;
};

/**
 * Get a single blog post by slug with full content
 */
export const getBlogBySlug = async (slug: string) => {
	const entry = blogIndex.find((b) => b.slug === slug);

	if (!entry) {
		throw new BlogNotFoundError(slug);
	}

	try {
		// Dynamically import the individual content file
		const filePath = `/src/content/compiled/${slug}.json`;
		const loadContent = blogContentModules[filePath];

		if (!loadContent) {
			throw new Error(`Content file not found: ${filePath}`);
		}

		const contentFile = await loadContent();

		return {
			id: entry.id,
			title: entry.title,
			description: entry.description,
			date: entry.date,
			slug: entry.slug,
			image: entry.image,
			featured: entry.featured || false,
			tags: entry.tags || [],
			keywords: entry.keywords || [],
			content: contentFile.default.content,
			htmlContent: contentFile.default.htmlContent,
		} satisfies BlogPost;
	} catch (error) {
		throw new BlogImportError(slug, error);
	}
};

/**
 * Get all featured blog posts
 */
export const getFeaturedBlogs = async () => {
	const allBlogs = await getAllBlogs();
	return allBlogs.filter((blog) => blog.featured);
};

/**
 * Get related blog posts based on shared tags
 * @param slug - The slug of the current blog post
 * @param limit - Maximum number of related posts to return (default: 3)
 */
export const getRelatedBlogs = async (slug: string, limit = 3) => {
	const allBlogs = await getAllBlogs();
	const currentBlog = allBlogs.find((blog) => blog.slug === slug);

	if (!currentBlog?.tags?.length) {
		// If no current blog or no tags, return recent posts (excluding current)
		return allBlogs.filter((blog) => blog.slug !== slug).slice(0, limit);
	}

	// Calculate relevance score for each blog based on shared tags
	const blogsWithScore = allBlogs
		.filter((blog) => blog.slug !== slug) // Exclude current blog
		.map((blog) => {
			const sharedTags =
				blog.tags?.filter((tag) => currentBlog.tags.includes(tag)) || [];
			return {
				blog,
				score: sharedTags.length,
			};
		})
		.filter((item) => item.score > 0); // Only include blogs with shared tags

	// Sort by score (descending) and then by date (newest first)
	blogsWithScore.sort((a, b) => {
		if (b.score !== a.score) {
			return b.score - a.score;
		}
		const dateA = new Date(a.blog.date);
		const dateB = new Date(b.blog.date);
		return dateB.getTime() - dateA.getTime();
	});

	const relatedBlogs = blogsWithScore.map((item) => item.blog).slice(0, limit);

	// If we don't have enough related blogs, fill with recent posts
	if (relatedBlogs.length < limit) {
		const additionalBlogs = allBlogs
			.filter(
				(blog) =>
					blog.slug !== slug &&
					!relatedBlogs.find((rb) => rb.slug === blog.slug),
			)
			.slice(0, limit - relatedBlogs.length);

		relatedBlogs.push(...additionalBlogs);
	}

	return relatedBlogs;
};

/**
 * Get blog posts by tag
 */
export const getBlogsByTag = async (tag: string) => {
	const allBlogs = await getAllBlogs();
	return allBlogs.filter((blog) => blog.tags.includes(tag));
};

/**
 * Get all unique tags from all blog posts
 */
export const getAllTags = async () => {
	const allBlogs = await getAllBlogs();
	const tagsSet = new Set<string>();

	for (const blog of allBlogs) {
		for (const tag of blog.tags) {
			tagsSet.add(tag);
		}
	}

	return Array.from(tagsSet).sort();
};

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
	const date = new Date(dateString);

	// Check if date is valid
	if (Number.isNaN(date.getTime())) {
		return dateString; // Return original if invalid
	}

	return date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric",
	});
}
